import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import type { Aggregation } from '../PositionAggregationsService.ts';
import { createAggregations } from '../../../management/UseUserAggregations.tsx';
import { calculateAllAggregations, type PortfolioSunburstPosition } from './PortfolioSnapshotSunburstContainer.tsx';
import { useCurrentExposureSettingsInputSuspenseQuery } from '../../../../generated/graphql.tsx';
import WidgetSettings from 'components/management/customDashboard/WidgetSettings.tsx';
import { updateWidgetState, useWidgetState } from 'components/management/customDashboard/CustomWidgetContext.tsx';
import gYupResolver from '../../../technical/form/gYupResolver.ts';
import { type AggItem, AggregationList } from '../AggregationList.tsx';
import { useState } from 'react';
import { FormPortfolioFilters } from 'components/technical/form/FormPortfolioFilters.tsx';
import {
  type PortfolioFilters,
  portfolioFiltersSchema,
} from 'components/technical/PortfolioFilterDrawer/UsePortfolioFilters.tsx';

const schema = yup
  .object({
    title: yup.string().required(),
  })
  .concat(portfolioFiltersSchema);

type FormState = {
  title: string;
} & Partial<PortfolioFilters>;

export type CurrentExposureWidgetState = {
  title: string;
  aggs: { active: boolean; category: string }[];
} & Partial<PortfolioFilters>;

const CurrentExposureSettings = ({
  id,
  onFinishedEditing,
  onRemovedWidget,
  title,
  activeAggsOrdered,
  allAggs,
  ...portfolioFilters
}: {
  id: string;
  onFinishedEditing: () => void;
  onRemovedWidget: () => Promise<void>;
  title: string;
  activeAggsOrdered: string[];
  allAggs: Aggregation<PortfolioSunburstPosition>[];
} & Partial<PortfolioFilters>) => {
  const updater = updateWidgetState<CurrentExposureWidgetState>();
  // kept outside the state, always valid, so no validation required
  const [items, setItems] = useState<AggItem[]>(
    allAggs.map((agg) => ({
      id: agg.category,
      label: agg.label,
      category: agg.category,
      checked: activeAggsOrdered.includes(agg.category),
    }))
  );

  const methods = useForm<FormState>({
    resolver: gYupResolver(schema),
    defaultValues: {
      title,
      ...portfolioFilters,
    },
  });

  const handleFormSubmit = async (input: FormState): Promise<void> => {
    await updater({
      id,
      state: {
        ...portfolioFilters,
        title: input.title,
        aggs: items.map((item) => ({
          category: item.category,
          active: item.checked,
        })),
      },
      updateBackend: true,
    });

    onFinishedEditing();
  };

  return (
    <WidgetSettings methods={methods} handleFormSubmit={handleFormSubmit} onRemovedWidget={onRemovedWidget}>
      <FormPortfolioFilters />
      <AggregationList aggs={items} setAggs={setItems} />
    </WidgetSettings>
  );
};

export const CurrentExposureSettingsContainer = ({
  id,
  onFinishedEditing,
  onRemovedWidget,
}: {
  id: string;
  onFinishedEditing: () => void;
  onRemovedWidget: () => Promise<void>;
}) => {
  const initialState = useWidgetState<CurrentExposureWidgetState>(id);
  const { data } = useCurrentExposureSettingsInputSuspenseQuery();
  const { allAggs, activeAggregationsOrdered } = createAggregations({
    savedAggs: initialState.aggs ?? [],
    defaultAggs: [],
    allAvailableAggregations: calculateAllAggregations(data.assets.assetGroups),
  });

  return (
    <CurrentExposureSettings
      {...initialState}
      id={id}
      onFinishedEditing={onFinishedEditing}
      onRemovedWidget={onRemovedWidget}
      activeAggsOrdered={activeAggregationsOrdered}
      allAggs={allAggs}
    />
  );
};
